import { useEffect, useRef, useState } from "react";
import { Camera, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, fetchProducts } from "@/lib/api";

function PhotoRow({ p, onDone }) {
    const ref = useRef(null);
    const [busy, setBusy] = useState(false);

    const upload = async (file) => {
        if (!file) return;
        if (file.size > 8 * 1024 * 1024) {
            toast.error("Photo is larger than 8 MB");
            return;
        }
        setBusy(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            await api.post(`/partner/product-photo/${p.slug}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
            toast.success(`${p.name} photo is live`);
            onDone();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Upload failed");
        } finally {
            setBusy(false);
            if (ref.current) ref.current.value = "";
        }
    };

    return (
        <div className="flex items-center gap-4 py-3 border-b border-cream/10" data-testid={`photo-row-${p.slug}`}>
            <img src={p.image} alt={p.name} className="w-16 h-16 object-cover rounded-sm" />
            <div className="flex-1 min-w-0">
                <p className="text-cream font-medium">{p.name}</p>
                <p className="text-xs text-cream">{p.image?.startsWith("/api/") ? "Your photo" : "Stand-in photo"}</p>
            </div>
            <input ref={ref} type="file" accept="image/jpeg,image/png,image/webp" className="hidden"
                onChange={(e) => upload(e.target.files?.[0])} data-testid={`photo-input-${p.slug}`} />
            <button onClick={() => ref.current?.click()} disabled={busy} className="btn-pill btn-ghost text-[0.6rem]"
                data-testid={`photo-upload-btn-${p.slug}`}>
                {busy ? <Loader2 size={12} className="animate-spin" /> : <Camera size={12} />} Upload photo
            </button>
        </div>
    );
}

export function ProductPhotosCard() {
    const [products, setProducts] = useState([]);
    const load = () => fetchProducts().then(setProducts).catch(() => setProducts([]));
    useEffect(() => { load(); }, []);

    return (
        <div className="mb-8 p-5 border border-cream/15 rounded-sm" data-testid="product-photos-card">
            <p className="text-[0.62rem] tracking-[0.3em] uppercase text-gold inline-flex items-center gap-1.5">
                <Camera size={12} /> Product photos
            </p>
            <p className="mt-2 text-sm text-cream">Upload a JPG, PNG or WebP (up to 8 MB). It replaces the stand-in photo on the site right away.</p>
            <div className="mt-3">
                {products.map((p) => <PhotoRow key={p.slug} p={p} onDone={load} />)}
            </div>
        </div>
    );
}
